import React, { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getEBibliotheek, deleteEBibliotheek, createEBibliotheek, updateEBibliotheek, setEbookOffline } from "../../../utils/api";
import { useOfflineColumn, OfflineField } from "../../../components/OfflineToggle/OfflineToggle";

// Field names match the backend model (Backendsrv/Models/bibliotheekModel.js).
const FILE_TYPES = ["pdf", "epub", "mobi", "other"];

const initialFormData = {
  title: "",
  description: "",
  category: "",
  parentcategory: "",
  filetype: "pdf",
  coverImageUrl: "",
  offlineAllowed: false,
};

const uniqueSorted = (values) => {
  const seen = new Map();
  values.forEach((v) => {
    const clean = String(v || "").trim();
    if (clean && !seen.has(clean.toLowerCase())) seen.set(clean.toLowerCase(), clean);
  });
  return [...seen.values()].sort((a, b) => a.localeCompare(b, "nl"));
};

const NEW_VALUE = "__new__";

// Select from existing values, or type a new one (prevents typos).
function ExistingValueSelect({ label, name, value, options, onChange, disabled, hint }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const inList = !value || options.some((o) => o === value);

  const confirmNew = () => {
    const clean = draft.trim();
    if (clean) {
      const existing = options.find((o) => o.toLowerCase() === clean.toLowerCase());
      onChange(name, existing || clean);
    }
    setAdding(false);
    setDraft("");
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      {adding ? (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmNew(); } }}
            placeholder={`Nieuwe ${label.toLowerCase()}`}
          />
          <button type="button" className="btn btn-primary" onClick={confirmNew}>OK</button>
          <button type="button" className="btn btn-secondary" onClick={() => setAdding(false)}>Annuleren</button>
        </div>
      ) : (
        <select
          name={name}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.value === NEW_VALUE) setAdding(true);
            else onChange(name, e.target.value);
          }}
        >
          <option value="">(Geen)</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          {!inList && <option value={value}>{value}</option>}
          <option value={NEW_VALUE}>+ Nieuwe toevoegen…</option>
        </select>
      )}
      {hint && <small style={{ color: "#6b4d39" }}>{hint}</small>}
    </div>
  );
}

const fileNameOf = (url) => {
  if (!url) return "";
  try { return decodeURIComponent(String(url).split("/").pop().split("?")[0]); } catch (_e) { return String(url).split("/").pop(); }
};

const safeUrl = (url) => {
  try { return encodeURI(decodeURI(url)); } catch (_e) { return encodeURI(url); }
};

export default function EBibliotheek() {
  const offlineColumn = useOfflineColumn("ebibliotheek.offline", setEbookOffline);
  const [books, setBooks] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [pdfFile, setPdfFile] = useState(null);
  const [coverChanged, setCoverChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listKey, setListKey] = useState(0);

  const loadBooks = useCallback(async () => {
    try {
      const data = await getEBibliotheek();
      setBooks(Array.isArray(data) ? data : (data?.items || []));
    } catch (_e) {
      setBooks([]);
    }
  }, []);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  const categoryOptions = useMemo(() => uniqueSorted(books.map((b) => b.category)), [books]);
  const parentOptions = useMemo(() => uniqueSorted(books.map((b) => b.parentcategory)), [books]);

  const columns = useMemo(() => [
    { key: "title", label: "Titel" },
    { key: "category", label: "Categorie" },
    { key: "parentcategory", label: "Parent category" },
    { key: "filetype", label: "Type", render: (v) => (v ? String(v).toUpperCase() : "-") },
    {
      key: "file",
      label: "PDF",
      render: (value) => (value ? (
        <a href={safeUrl(value)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          Bekijken
        </a>
      ) : "-"),
    },
    offlineColumn,
  ], [offlineColumn]);

  const filters = useMemo(() => [
    { key: "category", label: "Categorie", type: "select", options: categoryOptions.map((c) => ({ value: c, label: c })) },
    { key: "parentcategory", label: "Parent category", type: "select", options: parentOptions.map((c) => ({ value: c, label: c })) },
  ], [categoryOptions, parentOptions]);

  const handleAdd = () => {
    setFormData(initialFormData);
    setPdfFile(null);
    setCoverChanged(false);
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "",
      parentcategory: item.parentcategory || "",
      filetype: (item.filetype || "pdf").toLowerCase(),
      coverImageUrl: item.coverImageUrl || "",
      offlineAllowed: Boolean(item.offlineAllowed),
    });
    setPdfFile(null);
    setCoverChanged(false);
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const maxSize = 500;
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        setField("coverImageUrl", canvas.toDataURL("image/jpeg", 0.5));
        setCoverChanged(true);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditMode && !pdfFile) {
      alert("Kies een PDF-bestand.");
      return;
    }
    const data = new FormData();
    data.append("type", "Books"); // upload folder, must come before the file
    data.append("title", formData.title);
    data.append("description", formData.description || "");
    data.append("category", formData.category || "");
    data.append("parentcategory", formData.parentcategory || "");
    data.append("filetype", formData.filetype || "pdf");
    data.append("offlineAllowed", String(Boolean(formData.offlineAllowed)));
    if (formData.coverImageUrl && (!isEditMode || coverChanged)) data.append("coverImageUrl", formData.coverImageUrl);
    if (pdfFile) data.append("pdfFile", pdfFile);

    setLoading(true);
    try {
      if (isEditMode && selectedItem) {
        await updateEBibliotheek(selectedItem._id, data);
        alert("Bijgewerkt");
      } else {
        await createEBibliotheek(data);
        alert("Aangemaakt");
      }
      setModalOpen(false);
      setListKey((k) => k + 1);
      loadBooks();
    } catch (error) {
      alert(error.message || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  const currentFile = selectedItem?.file;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          key={listKey}
          title="E-Bibliotheek"
          fetchData={getEBibliotheek}
          deleteData={deleteEBibliotheek}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleEdit}
          filters={filters}
          searchPlaceholder="Zoek in e-bibliotheek..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "E-book bewerken" : "Nieuw e-book"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Boekgegevens</h3>
            <div className="form-group">
              <label>Titel *</label>
              <input type="text" name="title" value={formData.title} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Omschrijving</label>
              <textarea name="description" value={formData.description} onChange={handleFormChange} rows="3" />
            </div>
            <div className="form-row">
              <ExistingValueSelect
                label="Categorie"
                name="category"
                value={formData.category}
                options={categoryOptions}
                onChange={(name, value) => {
                  setField(name, value);
                  if (!value) setField("parentcategory", "");
                }}
              />
              <ExistingValueSelect
                label="Parent category"
                name="parentcategory"
                value={formData.parentcategory}
                options={parentOptions}
                onChange={setField}
                disabled={!formData.category}
                hint={!formData.category ? "Kies eerst een categorie." : undefined}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Bestand</h3>
            {currentFile && (
              <div className="form-group">
                <label>Huidige PDF</label>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <span>{fileNameOf(currentFile)}</span>
                  <a className="btn btn-secondary" href={safeUrl(currentFile)} target="_blank" rel="noopener noreferrer">
                    Bekijk PDF
                  </a>
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>{currentFile ? "Nieuwe PDF (optioneel, vervangt de huidige)" : "PDF *"}</label>
                <input
                  type="file"
                  accept="application/pdf"
                  className="file-input"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="form-group">
                <label>Bestandstype</label>
                <select name="filetype" value={formData.filetype} onChange={handleFormChange}>
                  {FILE_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Omslag</label>
              <input type="file" accept="image/*" onChange={handleImageChange} className="file-input" />
              {formData.coverImageUrl && (
                <div className="image-preview">
                  <img src={formData.coverImageUrl} alt="Omslag" style={{ maxWidth: "100px", maxHeight: "100px" }} />
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Beschikbaarheid</h3>
            <OfflineField
              permission="ebibliotheek.offline"
              checked={formData.offlineAllowed}
              onChange={(value) => setField("offlineAllowed", value)}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Opslaan..." : (isEditMode ? "Bijwerken" : "Aanmaken")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
